import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AppContext from '../../../Context/AppContext';
import requestAxios from '../../../util/requestAxios';
import {
    enablePortalV2Session,
    getPortalV2Paths,
    guardPortalV2Route,
    resolveUserStepPathV2,
} from '../../../util/portalV2';
import {
    initialFormAnswers,
    initialState,
} from '../../CustomizeQuestions/initialState';
import FindCoachModal from '../../Dashboard/MyOolaCoach/FindCoachModal/FindCoachModal';
import ModalTimeZoneV2 from './ModalTimeZoneV2';
import '../portalV2Shared.css';
import './CustomizeQuestionsV2.css';

/**
 * Portal V2 customize questions — parallel to /customizeQuestions.
 * Same APIs; V2 visual language.
 *
 * Screenshot helpers (dev only):
 *   /customizeQuestions-v2?preview=tz        → force timezone modal
 *   /customizeQuestions-v2?preview=customize → customize form (skip step gate)
 */
const CustomizeQuestionsV2 = () => {
    const { checkUserSession } = useContext(AppContext);
    const history = useHistory();
    const session = checkUserSession() || {};
    const { userId, token, firstName, userStepId, userTypeId } = session;

    const previewMode = useMemo(() => {
        try {
            const q = new URLSearchParams(window.location.search).get('preview');
            return q === 'tz' || q === 'customize' ? q : null;
        } catch {
            return null;
        }
    }, []);

    const [questions, setQuestions] = useState(
        initialState.questionsSection.questions
    );
    const [formAnswers, setFormAnswers] = useState(initialFormAnswers);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [showCoach, setShowCoach] = useState(false);
    const [showTimezone, setShowTimezone] = useState(previewMode === 'tz');
    const [mobilePane, setMobilePane] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const touchStartX = useRef(null);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        const sync = () => setIsMobile(mq.matches);
        sync();
        if (mq.addEventListener) mq.addEventListener('change', sync);
        else mq.addListener(sync);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', sync);
            else mq.removeListener(sync);
        };
    }, []);

    const paths = getPortalV2Paths();
    const info = initialState.infoSection;
    const questionCount = questions.length;

    const incomplete = useMemo(
        () =>
            formAnswers.CustomizeQuestions.some((item) => item.OptionId === 0),
        [formAnswers]
    );

    const currentQuestion = questions[questionIndex];
    const currentSelected = currentQuestion
        ? formAnswers.CustomizeQuestions.find(
              (a) => a.questionId === currentQuestion.id
          )?.OptionId
        : 0;
    const isLastQuestion =
        questionCount > 0 && questionIndex >= questionCount - 1;
    const canAdvanceQuestion = Boolean(currentSelected);

    useEffect(() => {
        if (!guardPortalV2Route(history, '/customizeQuestions')) return undefined;
        enablePortalV2Session();
        let cancelled = false;
        (async () => {
            if (
                !previewMode &&
                userStepId !== 0 &&
                userTypeId !== 4
            ) {
                history.replace(resolveUserStepPathV2(userStepId));
                return;
            }
            setLoading(true);
            const { error: qErr, data } = await requestAxios({
                url: 'CustomizeAssessment/GetCustomizeQuestions',
                token,
            });
            if (cancelled) return;
            if (!qErr && Array.isArray(data) && data.length) {
                setQuestions(data);
                setFormAnswers({
                    userId,
                    CustomizeQuestions: data.map((q) => ({
                        questionId: q.id,
                        OptionId: 0,
                    })),
                });
                setQuestionIndex(0);
            }
            if (previewMode !== 'tz') {
                const { error: pErr, data: profile } = await requestAxios({
                    url: `Customer/GetCustomerProfileInfo/${userId}`,
                    token,
                });
                if (!cancelled && !pErr && profile && !profile.hasCoach) {
                    setShowCoach(true);
                }
            }
            if (!cancelled) {
                if (previewMode === 'tz') setShowTimezone(true);
                if (previewMode === 'customize') setShowTimezone(false);
                setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [history, token, userId, userStepId, userTypeId, previewMode]);

    const onSelect = (questionId, optionId) => {
        setFormAnswers((prev) => ({
            ...prev,
            CustomizeQuestions: prev.CustomizeQuestions.map((item) =>
                item.questionId === questionId
                    ? { ...item, OptionId: optionId }
                    : item
            ),
        }));
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        if (incomplete || busy) return;
        setBusy(true);
        setError('');
        const { error: saveErr } = await requestAxios({
            url: 'CustomizeAssessment/SaveCustomizeQuestions',
            method: 'post',
            token,
            data: { ...formAnswers, userId },
        });
        setBusy(false);
        if (saveErr) {
            setError('Could not save your answers. Please try again.');
            return;
        }
        history.push(paths.steps);
    };

    const goToIntro = () => {
        setMobilePane(0);
    };

    const goToQuestions = (index = 0) => {
        setQuestionIndex(
            Math.max(0, Math.min(index, Math.max(0, questionCount - 1)))
        );
        setMobilePane(1);
    };

    const goNextQuestion = () => {
        if (!canAdvanceQuestion || isLastQuestion) return;
        setQuestionIndex((prev) =>
            Math.min(prev + 1, Math.max(0, questionCount - 1))
        );
    };

    const goBack = () => {
        if (questionIndex > 0) {
            setQuestionIndex((prev) => prev - 1);
            return;
        }
        goToIntro();
    };

    const onTouchStart = (event) => {
        touchStartX.current = event.changedTouches[0]?.clientX ?? null;
    };

    const onTouchEnd = (event) => {
        if (touchStartX.current == null) return;
        const endX = event.changedTouches[0]?.clientX;
        if (endX == null) return;
        const delta = endX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(delta) < 56) return;

        if (mobilePane === 0) {
            if (delta < 0) goToQuestions(0);
            return;
        }

        if (delta < 0) {
            if (canAdvanceQuestion && !isLastQuestion) goNextQuestion();
            return;
        }
        goBack();
    };

    const totalDots = 1 + Math.max(questionCount, 1);
    const activeDot = mobilePane === 0 ? 0 : questionIndex + 1;

    return (
        <div className="pv2 pv2-customize">
            <div
                className={`pv2-customize__layout pv2-anim${
                    mobilePane === 1 ? ' is-pane-1' : ' is-pane-0'
                }`}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
            >
                <div className="pv2-customize__track">
                    <aside className="pv2-customize__intro pv2-glass">
                        <p className="pv2-eyebrow">Phase 1 · Design my life</p>
                        <h1 className="pv2-title">
                            Welcome{firstName ? `, ${firstName}` : ''}
                        </h1>
                        <p className="pv2-sub">
                            Three quick answers so we can tailor your blueprint.
                            Then you&apos;ll move through Where you are, Where
                            you&apos;re going, and How you get there.
                        </p>
                        <ul
                            className="pv2-customize__expect"
                            aria-label="Your journey"
                        >
                            {info.stepsInfo.map((step, stepIndex) => {
                                const title = String(step.title)
                                    .replace(/<[^>]+>/g, '')
                                    .trim();
                                const isActive = stepIndex === 0;
                                const num = String(stepIndex + 1).padStart(
                                    2,
                                    '0'
                                );
                                return (
                                    <li
                                        key={step.text}
                                        className={`pv2-customize__step${
                                            isActive ? ' is-active' : ''
                                        }`}
                                    >
                                        <span
                                            className="pv2-customize__step-num"
                                            aria-hidden
                                        >
                                            {num}
                                        </span>
                                        <span className="pv2-customize__step-body">
                                            <strong>{title}</strong>
                                            <span>{step.text}</span>
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                        <button
                            type="button"
                            className="pv2-btn pv2-btn--primary pv2-customize__next"
                            onClick={() => goToQuestions(0)}
                        >
                            Next →
                        </button>
                    </aside>

                    <form
                        className={`pv2-customize__form pv2-glass pv2-glass--strong${
                            isLastQuestion ? ' is-q-last' : ''
                        }`}
                        onSubmit={onSubmit}
                        style={{
                            '--pv2-q-count': Math.max(questionCount, 1),
                            '--pv2-q-index': questionIndex,
                        }}
                    >
                        <button
                            type="button"
                            className="pv2-customize__back"
                            onClick={goBack}
                        >
                            {questionIndex > 0
                                ? '← Previous'
                                : '← Instructions'}
                        </button>
                        <h2>Customize your experience</h2>
                        <p className="pv2-customize__form-sub pv2-customize__form-sub--desktop">
                            Answer all three to unlock Step 1.
                        </p>
                        <p className="pv2-customize__form-sub pv2-customize__form-sub--mobile">
                            Question {questionIndex + 1} of{' '}
                            {Math.max(questionCount, 1)}
                        </p>

                        {loading ? (
                            <p className="pv2-sub">Loading questions…</p>
                        ) : null}
                        {error ? <p className="pv2-error">{error}</p> : null}

                        <div className="pv2-customize__q-slider">
                            <div className="pv2-customize__q-track">
                                {!loading
                                    ? questions.map((question, index) => {
                                          const selected =
                                              formAnswers.CustomizeQuestions.find(
                                                  (a) =>
                                                      a.questionId ===
                                                      question.id
                                              )?.OptionId;
                                          return (
                                              <fieldset
                                                  key={question.id}
                                                  className="pv2-customize__q"
                                                  aria-hidden={
                                                      isMobile &&
                                                      index !== questionIndex
                                                          ? true
                                                          : undefined
                                                  }
                                              >
                                                  <legend>
                                                      <span>
                                                          {String(
                                                              index + 1
                                                          ).padStart(2, '0')}
                                                      </span>
                                                      {question.text}
                                                  </legend>
                                                  <div className="pv2-customize__options">
                                                      {(
                                                          question.options || []
                                                      ).map((opt) => (
                                                          <button
                                                              key={opt.id}
                                                              type="button"
                                                              tabIndex={
                                                                  isMobile &&
                                                                  index !==
                                                                      questionIndex
                                                                      ? -1
                                                                      : undefined
                                                              }
                                                              className={`pv2-customize__option${
                                                                  selected ===
                                                                  opt.id
                                                                      ? ' is-selected'
                                                                      : ''
                                                              }`}
                                                              onClick={() =>
                                                                  onSelect(
                                                                      question.id,
                                                                      opt.id
                                                                  )
                                                              }
                                                          >
                                                              {opt.text}
                                                          </button>
                                                      ))}
                                                  </div>
                                              </fieldset>
                                          );
                                      })
                                    : null}
                            </div>
                        </div>

                        <button
                            type="button"
                            className="pv2-btn pv2-btn--primary pv2-customize__q-next"
                            disabled={!canAdvanceQuestion || loading}
                            onClick={goNextQuestion}
                        >
                            Next →
                        </button>
                        <button
                            type="submit"
                            className="pv2-btn pv2-btn--primary pv2-customize__submit"
                            disabled={incomplete || busy || loading}
                        >
                            {busy ? 'Saving…' : 'Get started →'}
                        </button>
                    </form>
                </div>

                <div
                    className="pv2-customize__dots"
                    aria-label="Customize steps"
                >
                    {Array.from({ length: totalDots }, (_, index) => {
                        const isIntro = index === 0;
                        const label = isIntro
                            ? 'Instructions'
                            : `Question ${index}`;
                        return (
                            <button
                                key={label}
                                type="button"
                                className={`pv2-customize__dot${
                                    activeDot === index ? ' is-active' : ''
                                }`}
                                aria-label={label}
                                aria-current={
                                    activeDot === index ? 'step' : undefined
                                }
                                onClick={() => {
                                    if (isIntro) goToIntro();
                                    else goToQuestions(index - 1);
                                }}
                            />
                        );
                    })}
                </div>
            </div>

            <ModalTimeZoneV2
                open={showTimezone}
                onOpenChange={setShowTimezone}
            />
            {showCoach ? (
                <FindCoachModal
                    show={showCoach}
                    setShowModal={setShowCoach}
                    coachId={0}
                />
            ) : null}
        </div>
    );
};

export default CustomizeQuestionsV2;
